import { ChangeDetectorRef } from '@angular/core';
import {
  AbcDateBoxComponent,
  AbcDropDownBoxComponent,
  AbcNumberBoxComponent,
  AbcTextBoxComponent,
  DateBoxType,
  _AbcCheckBoxComponent,
  _AbcGridComponent,
} from '@barthauer/abc/ui-components';

import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { TargetColumnValue } from '@barthauer/abc/core/data';
import { debounceTime } from 'rxjs/operators';
import { ListDataSource } from '../../../models/data.model';
import { SpecialType } from '../../../models/layout.model';
import { LayoutColumnViewModel } from '../../../viewModels/layout.viewModel';
import { DataFormService } from '../../data-form.service';
import { DataFormSettings } from '../settings.model';
import { UiCreatorBase } from './ui-creator-base';

export class StandardUiCreator extends UiCreatorBase {
  constructor(
    private service: DataFormService,
    private settings: DataFormSettings,
    private cdr?: ChangeDetectorRef,
  ) {
    super(service, settings);
  }
  public createAbcCheckbox(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);

    const abcCheckbox = this.service.abcCoreContainer.createComponent(this.tabsDirective, _AbcCheckBoxComponent);

    // Applying required field color for the components box
    if (column.backColor) {
      const controlBackgroundColorVariable = getComputedStyle(document.documentElement)
        .getPropertyValue('--abcControlBackgroundColor')
        .trim();

      const boxCssText =
        CompareHelper.isNullorEmpty(controlBackgroundColorVariable) || column.backColor === 'transparent'
          ? 'background-color: ' + column.backColor + '!important;'
          : 'background-color: var(--abcControlBackgroundColor) !important;';

      setTimeout(() => {
        const boxEl: HTMLElement = abcCheckbox.domElement.querySelector('.dx-checkbox-icon');
        boxEl.style.cssText += boxCssText;
      }, 0);
    }
    //

    const abcCheckboxRef: _AbcCheckBoxComponent = <_AbcCheckBoxComponent>abcCheckbox.componentRef.instance;
    abcCheckboxRef.height = '100%';
    abcCheckboxRef.width = '100%';
    abcCheckboxRef.disabled = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;
    abcCheckboxRef.text = column.caption;

    abcCheckboxRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;

    abcCheckboxRef.onValueChanged.subscribe((result) => {
      this.setCurrentColumnValue(column, result.value);
      this.validateColumnValue(column, result.value);
    });

    this.applyHtmlStyle(td, column);
    this.appendContextMenu(abcCheckbox.domElement, column);
    this.addColumnForValidation(column, abcCheckboxRef);

    return {
      domElement: abcCheckbox.domElement,
      componentInstance: abcCheckboxRef,
    };
  }

  public createAbcDateBox(td: HTMLTableCellElement, column: LayoutColumnViewModel, type?: DateBoxType) {
    this.setTdcolAndRowSpan(td, column);
    const c = this.settings.mainObjectTypeViewModel.columnsByName.get(column.oPath);
    const abcDateBox = this.service.abcCoreContainer.createComponent(this.tabsDirective, AbcDateBoxComponent);
    const abcDateBoxRef: AbcDateBoxComponent = <AbcDateBoxComponent>abcDateBox.componentRef.instance;
    abcDateBoxRef.label = column.caption;
    abcDateBoxRef.acceptCustomValue = true;
    abcDateBoxRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;
    abcDateBoxRef.focusStateEnabled = false;
    if (type) {
      abcDateBoxRef.type = type;
    }
    abcDateBoxRef.onValueChanged.subscribe((e) => {
      let value = e.value;
      if (abcDateBoxRef.type === DateBoxType.Date) {
        value = ConvertHelper.toISODateString(value);
      } else if (abcDateBoxRef.type === DateBoxType.Time) {
        value = ConvertHelper.toISOTimeString(value);
      }
      this.setCurrentColumnValue(column, value);
      this.validateColumnValue(column, e.value);
    });
    abcDateBoxRef.showClearButtonOnFocusOnly = !c?.isRequired;
    abcDateBoxRef.onChange.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        // this.setCurrentColumnValue(column, result);
        if (abcDateBoxRef.type === DateBoxType.Time) {
          this.isValidTime(abcDateBoxRef, column.oPath);
        } else {
          this.isValidDate(abcDateBoxRef, column.oPath);
        }
      }
    });
    abcDateBoxRef.onInput.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        this.setCurrentColumnValue(column, result.event.target.value);
      }
    });
    abcDateBoxRef.onClosed.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        const result = abcDateBoxRef.value;
        if (abcDateBoxRef.type === DateBoxType.Time) {
          this.isValidTime(abcDateBoxRef, column.oPath);
        } else {
          this.isValidDate(abcDateBoxRef, column.oPath);
        }
      }
    });

    this.applyHtmlStyle(abcDateBox.domElement, column);
    this.appendContextMenu(abcDateBox.domElement, column);
    this.addColumnForValidation(column, abcDateBoxRef);
    this.settings.dateBoxListMap.set(type === DateBoxType.Time ? 'TIME-' : 'DATE-' + column.oPath, abcDateBoxRef);

    return {
      domElement: abcDateBox.domElement,
      componentInstance: abcDateBoxRef,
    };
  }

  public createAbcTextBox(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);
    const abcTextBox = this.service.abcCoreContainer.createComponent(this.tabsDirective, AbcTextBoxComponent);
    const abcTextBoxRef: AbcTextBoxComponent = <AbcTextBoxComponent>abcTextBox.componentRef.instance;
    abcTextBoxRef.label = column.caption;
    abcTextBoxRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;
    abcTextBoxRef.focusStateEnabled = false;
    // abcTextBoxRef.enableTootip = true;
    abcTextBoxRef.onValueChanged.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        this.setCurrentColumnValue(column, result.value);
        this.validateColumnValue(column, result.value);
      }
    });
    abcTextBoxRef.onInput.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        this.setCurrentColumnValue(column, result.event.target.value);
      }
    });

    this.applyHtmlStyle(abcTextBox.domElement, column);
    this.appendContextMenu(abcTextBox.domElement, column);
    this.addColumnForValidation(column, abcTextBoxRef);

    return {
      domElement: abcTextBox.domElement,
      componentInstance: abcTextBoxRef,
    };
  }

  public createAbcNumberBox(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);
    const abcNumberBox = this.service.abcCoreContainer.createComponent(this.tabsDirective, AbcNumberBoxComponent);
    const abcNumberBoxRef: AbcNumberBoxComponent = <AbcNumberBoxComponent>abcNumberBox.componentRef.instance;
    abcNumberBoxRef.label = column.caption;
    abcNumberBoxRef.step = 0;
    abcNumberBoxRef.format = _AbcGridComponent.getFormatFromEditMask(
      column.formatSettings?.EditMask.replace(/\\/g, ''),
    );
    abcNumberBoxRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly ? true : false;
    abcNumberBoxRef.focusStateEnabled = true;
    abcNumberBoxRef.onValueChanged.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        this.setCurrentColumnValue(column, result.value);
      }
      this.validateColumnValue(column, result.value);
    });
    abcNumberBoxRef.onInput.subscribe((result) => {
      if (!(column.isReadOnly || this.settings.layoutViewModel.isReadOnly)) {
        this.setCurrentColumnValue(column, result.event.target.value);
      }
    });

    this.applyHtmlStyle(abcNumberBox.domElement, column);
    this.appendContextMenu(abcNumberBox.domElement, column);
    this.addColumnForValidation(column, abcNumberBoxRef);

    return {
      domElement: abcNumberBox.domElement,
      componentInstance: abcNumberBoxRef,
    };
  }

  public createLinkLabel(td: HTMLTableCellElement, column: LayoutColumnViewModel) {
    this.setTdcolAndRowSpan(td, column);
    const abcTextBox = this.service.abcContainer.createComponent(this.tabsDirective, AbcTextBoxComponent);
    const abcTextBoxRef: AbcTextBoxComponent = <AbcTextBoxComponent>abcTextBox.componentRef.instance;
    abcTextBox.domElement.style.whiteSpace = 'nowrap';
    abcTextBoxRef.focusStateEnabled = false;
    // abcTextBoxRef.enableTootip = true;
    abcTextBoxRef.labelMode = 'static';
    abcTextBoxRef.label = column.caption;
    abcTextBoxRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;
    abcTextBoxRef.buttons = [];
    abcTextBoxRef.buttons.push({
      location: 'after',
      name: 'edit',
      options: {
        icon: 'edit',
        stylingMode: 'text',
        onClick: () => {
          const link = this.settings.linkLabelsMap.get(column.dataField).querySelector('a') as HTMLElement;
          const editButton = this.settings.linkLabelsMap
            .get(column.dataField)
            .getElementsByClassName('dx-button')[0] as HTMLElement;
          link.setAttribute('stylingMode', 'text');
          if (link.contentEditable === 'false') {
            link.setAttribute('contenteditable', 'true');
            editButton.style.backgroundColor = 'var(--PrimaryMainShading10, #fff)';
          } else {
            link.setAttribute('contenteditable', 'false');
            editButton.style.backgroundColor = 'transparent';
          }
        },
      },
    });
    abcTextBoxRef.onValueChanged.pipe(debounceTime(0)).subscribe((result) => {
      abcTextBoxRef.value = result.value;
      this.setCurrentColumnValue(column, result.value);
      const a = abcTextBox.domElement.getElementsByTagName('a')[0];
      if (a) {
        const href = a.href;
        const indexSlash = href?.lastIndexOf('/');
        if (href?.includes('null') || !abcTextBoxRef.value) {
          a.href = '';
          a.addEventListener('click', (e: any) => {
            if (e.target.pathname.includes('/')) {
              e.preventDefault();
            }
          });
        }
        if (abcTextBoxRef?.value && column.specialType === SpecialType.LinkLabelEmail) {
          a.href = 'mailto:' + href?.slice(indexSlash + 1, href.length);
        } else {
          if (this.service.core.defaultTargetWindowName) {
            a.target = this.service.core.defaultTargetWindowName;
          }
        }
      }
      if (result.previousValue) {
        if (this.settings.isPendingChangesReverted) {
          this.settings.linkLabelsMap.forEach((links, key) => {
            const node = links.getElementsByClassName('dx-texteditor-input')[0] as HTMLAnchorElement;
            if (node) {
              if (!(column.caption && column.specialType === SpecialType.LinkLabel)) {
                node.innerText = this.settings.listOfValueWrappers[key].value;
              }
              node.href = this.settings.listOfValueWrappers[key].value;
            }
            if (a && abcTextBoxRef?.value && column.specialType === SpecialType.LinkLabelEmail) {
              a.href = 'mailto:' + this.settings.listOfValueWrappers[key].value;
            }
            this.service.cdrSubject.next(null);
          });
          this.settings.isPendingChangesReverted = false;
        }
      }
      this.validateColumnValue(column, result.value);
    });
    this.setTdcolAndRowSpan(td, column);
    this.settings.linkLabelsMap.set(column.dataField, abcTextBox.domElement);

    this.applyHtmlStyle(abcTextBox.domElement, column);
    this.appendContextMenu(abcTextBox.domElement, column);
    this.addColumnForValidation(column, abcTextBoxRef);

    return {
      domElement: abcTextBox.domElement,
      componentInstance: abcTextBoxRef,
    };
  }

  public prepareComboBoxSource(data: ListDataSource) {
    const lists = new ListDataSource(data);
    const columnHeader = [];
    for (let index = 0; index < lists.ColumnInfos?.length; index++) {
      const element = lists.ColumnInfos[index];
      if (element.Name === 'Id' || element.Name === this.settings.DISPLAYTEXT_HIDDEN) {
        columnHeader.push({
          dataField: element.Name,
          name: element.Name,
          type: 'string',
          visible: false,
        });
      } else {
        columnHeader.push({
          dataField: element.Name,
          name: element.Caption,
          caption: element.Caption,
          type: 'string',
        });
      }
    }
    const gridData = lists.Data;

    return { columns: columnHeader, data: gridData };
  }

  public async createAbcDropDownBox(
    td: HTMLTableCellElement,
    column: LayoutColumnViewModel,
    picklistName?: string,
  ): Promise<{
    domElement: HTMLElement;
    componentInstance: AbcDropDownBoxComponent;
  }> {
    this.setTdcolAndRowSpan(td, column);
    const abcDropDownBox = this.service.abcCoreContainer.createComponent(this.tabsDirective, AbcDropDownBoxComponent);
    const abcDropDownRef: AbcDropDownBoxComponent = <AbcDropDownBoxComponent>abcDropDownBox.componentRef.instance;
    abcDropDownRef.label = column.caption;
    abcDropDownRef.rtlEnabled = this.service.arena.rtlEnabled;
    abcDropDownRef.abcGrid.rtlEnabled = this.service.arena.rtlEnabled;
    abcDropDownRef.readOnly = column.isReadOnly || this.settings.layoutViewModel.isReadOnly;
    if (column.isRefTextEditable) {
      abcDropDownRef.acceptCustomValue = true;
    }

    if (column.refListSettings?.EditValueExpression) {
      this.settings.customComboBoxColumns.push(column);
    }

    if (abcDropDownRef.readOnly) {
      abcDropDownRef.showDropDownButton = false;
    }

    const columnName = column.oPath.replace('ref:', '');
    const c = this.settings.mainObjectTypeViewModel.columnsByName.get(columnName);
    if (picklistName) {
      await abcDropDownRef.setupForPicklistByName(
        picklistName,
        this.settings.listOfValueWrappers[column.dataField]?.initialValue?.toString(),
        this.settings.listOfValueWrappers[column.dataField]?.initialDisplayText,
      );
      this.settings.setCurrentObjectFunctions.push(async (object) => {
        await abcDropDownRef.updateForPicklistByName(
          this.settings.listOfValueWrappers[column.dataField]?.initialValue?.toString(),
          this.settings.listOfValueWrappers[column.dataField]?.initialDisplayText,
        );
      });
    } else {
      await abcDropDownRef.setupForRefColumn(
        this.settings.mainObjectTypeViewModel.name,
        columnName,
        this.settings.selectedObject?.Object?.Id,
        this.settings.listOfValueWrappers[column.dataField]?.initialValue?.toString(),
        this.settings.listOfValueWrappers[column.dataField]?.initialDisplayText,
        undefined,
        undefined,
        undefined,
        column.refListSettings?.EditValueExpression,
        column.refListSettings?.DisplayTextExpression,
      );
      this.settings.setCurrentObjectFunctions.push(async (object) => {
        await abcDropDownRef.updateForRefColumn(
          object?.Id,
          this.settings.listOfValueWrappers[column.dataField]?.initialValue?.toString() ?? '',
          this.settings.listOfValueWrappers[column.dataField]?.initialDisplayText,
          () => this.getTargetColumnValues(),
        );
      });
    }

    abcDropDownRef.showClearButtonOnFocusOnly = !c?.isRequired;

    abcDropDownRef.onValueChanged.subscribe((result) => {
      this.setCurrentColumnValue(column, result?.value);
      this.validateColumnValue(column, result.value);
    });

    this.applyHtmlStyle(abcDropDownBox.domElement, column);
    this.appendContextMenu(abcDropDownBox.domElement, column);
    this.addColumnForValidation(column, abcDropDownRef);

    return {
      domElement: abcDropDownBox.domElement,
      componentInstance: abcDropDownRef,
    };
  }

  protected override getTargetColumnValues(): TargetColumnValue[] {
    const columns = this.service.persistence.getPendingObjectPackage(this.settings.objectKey);
    const targetColumnValues = this.extractNewValuesToArray(columns?.fieldValueHistories);
    const layoutColumns = this.settings?.customComboBoxColumns;
    if (layoutColumns) {
      for (const c of layoutColumns) {
        if (c.refListSettings?.EditValueExpression) {
          const columnName = c.oPath?.replace('ref:', '');
          if (!targetColumnValues.find((_) => _.Name === columnName)) {
            targetColumnValues.push({
              Name: columnName,
              Value: this.settings.listOfValueWrappers[c.dataField]?.initialValue,
            });
          }
        }
      }
    }
    return targetColumnValues;
  }

  private extractNewValuesToArray(originalObject: {}) {
    const newValuesArray: TargetColumnValue[] = [];
    if (originalObject) {
      for (const key in originalObject) {
        if (Object.prototype.hasOwnProperty.call(originalObject, key)) {
          const nestedObject = originalObject[key];
          if (nestedObject && typeof nestedObject === 'object' && 'newValue' in nestedObject) {
            const targetColumnValue: TargetColumnValue = {
              Name: key,
              Value: nestedObject.newValue,
            };
            newValuesArray.push(targetColumnValue);
          }
        }
      }
    }
    return newValuesArray;
  }

  private setCurrentColumnValue(column: LayoutColumnViewModel, value: any) {
    this.settings.setCurrentColumnValue(column, value);
  }
}
